import React, { memo, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { useApp } from '../context/AppContext';
import { getMasteryLabel } from '../utils/noteHelpers';
import { INTERVALS } from '../hooks/useLocalStorage';

const ProgressPage = memo(function ProgressPage() {
  const navigate = useNavigate();
  const { notes, intervals, currentRange, streak, getStatsSummary, getIntervalStatsSummary } = useApp();
  
  const stats = getStatsSummary;
  const intervalStats = getIntervalStatsSummary;
  
  // Calculate per-note stats for unlocked notes
  const noteStats = useMemo(() => {
    return currentRange.unlockedNotes.map(note => {
      const data = notes[note];
      if (!data) {
        return { note, accuracy: 0, attempts: 0, avgTime: 0, mastery: 0 };
      }
      
      const total = data.correctCount + data.incorrectCount;
      const accuracy = total > 0 ? Math.round((data.correctCount / total) * 100) : 0;
      const avgTime = data.responseTimes.length > 0 
        ? Math.round(data.responseTimes.reduce((a, b) => a + b, 0) / data.responseTimes.length)
        : 0;
      
      return {
        note,
        accuracy,
        attempts: total,
        avgTime,
        mastery: data.masteryTier || 0
      };
    }).sort((a, b) => a.accuracy - b.accuracy);
  }, [notes, currentRange.unlockedNotes]);

  // Calculate per-interval stats
  const intervalStatsDetailed = useMemo(() => {
    return INTERVALS.map(interval => {
      const data = intervals[interval];
      if (!data) {
        return { interval, accuracy: 0, attempts: 0, mastery: 0 };
      }
      
      const total = data.correctCount + data.incorrectCount;
      const accuracy = total > 0 ? Math.round((data.correctCount / total) * 100) : 0;
      
      return {
        interval,
        accuracy,
        attempts: total,
        mastery: data.masteryTier || 0
      };
    });
  }, [intervals]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="min-h-screen bg-cream-100 paper-texture pb-8"
      data-testid="progress-screen"
    >
      <div className="max-w-lg mx-auto px-6 py-8">
        {/* Header */}
        <header className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="icon-btn w-10 h-10 rounded-full flex items-center justify-center transition-colors"
            style={{ backgroundColor: 'rgba(107, 127, 140, 0.1)' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.2)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.1)'}
            onMouseDown={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.3)'}
            onMouseUp={(e) => e.currentTarget.style.backgroundColor = 'rgba(107, 127, 140, 0.2)'}
            aria-label="Go back"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" style={{ color: '#6B7F8C' }} strokeWidth={1.5} />
          </button>
          <h1 className="font-serif text-2xl text-cream-900">Your Progress</h1>
        </header>
        
        {/* Overview Stats */}
        <section className="mb-8">
          <div className="grid grid-cols-2 gap-4">
            {/* Streak */}
            <Card className="border-wood-200 bg-white shadow-sm rounded-card">
              <CardContent className="p-4 text-center">
                <div className="flex justify-center mb-2">
                  <img src="/icon-streak.svg" alt="Streak" className="w-6 h-6" />
                </div>
                <p className="font-serif text-2xl text-cream-900">
                  {streak.currentCount} <span className="text-base font-serif text-cream-900">Days</span>
                </p>
                <p className="text-xs text-cream-900 font-sans font-semibold uppercase tracking-wider mt-1">
                  Streak
                </p>
              </CardContent>
            </Card>
            
            {/* Accuracy */}
            <Card className="border-wood-200 bg-white shadow-sm rounded-card">
              <CardContent className="p-4 text-center">
                <div className="flex justify-center mb-2">
                  <img src="/icon-accuracy.svg" alt="Accuracy" className="w-6 h-6" />
                </div>
                <p className="font-serif text-2xl text-cream-900">
                  {stats.accuracy}<span className="text-base">%</span>
                </p>
                <p className="text-xs text-cream-900 font-sans font-semibold uppercase tracking-wider mt-1">
                  Accuracy
                </p>
              </CardContent>
            </Card>
            
            {/* Sessions */}
            <Card className="border-wood-200 bg-white shadow-sm rounded-card">
              <CardContent className="p-4 text-center">
                <div className="flex justify-center mb-2">
                  <img src="/icon-sessions.svg" alt="Sessions" className="w-6 h-6" 
                    onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
                <p className="font-serif text-2xl text-cream-900">{stats.totalSessions}</p>
                <p className="text-xs text-cream-900 font-sans font-semibold uppercase tracking-wider mt-1">
                  Sessions
                </p>
              </CardContent>
            </Card>
            
            {/* Notes Mastered */}
            <Card className="border-wood-200 bg-white shadow-sm rounded-card">
              <CardContent className="p-4 text-center">
                <div className="flex justify-center mb-2">
                  <img src="/icon-mastery.svg" alt="Mastery" className="w-6 h-6" />
                </div>
                <p className="font-serif text-2xl text-cream-900">
                  {stats.masteredCount}<span className="text-base text-cream-800">/{stats.totalNotes}</span>
                </p>
                <p className="text-xs text-cream-900 font-sans font-semibold uppercase tracking-wider mt-1">
                  Mastery
                </p>
              </CardContent>
            </Card>
          </div>
        </section>
        
        {/* Mastery Progress */}
        <section className="mb-8">
          <h2 className="font-serif text-xl text-cream-900 mb-4">
            Mastery Progress
          </h2>
          
          <Card className="border-wood-200 bg-white shadow-sm rounded-card">
            <CardContent className="py-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-sans text-sm text-cream-800">
                  {stats.masteredCount} of {stats.totalNotes} notes mastered
                </span>
                <span className="font-serif text-lg text-cream-900">
                  {Math.round((stats.masteredCount / stats.totalNotes) * 100)}%
                </span>
              </div>
              <Progress 
                value={(stats.masteredCount / stats.totalNotes) * 100} 
                className="h-3 bg-cream-300"
                indicatorClassName="bg-[#5F8D4E]"
              />
            </CardContent>
          </Card>
        </section>
        
        {/* Per-Note Breakdown - List View Only */}
        <section className="mb-8">
          <h2 className="font-serif text-xl text-cream-900 mb-4">
            Note Performance
          </h2>
          
          <Card className="border-wood-200 bg-white shadow-sm rounded-card">
            <CardContent className="divide-y divide-wood-200">
              {noteStats.map(({ note, accuracy, attempts, mastery }) => (
                <div key={note} className="flex items-center justify-between py-3 first:pt-3 last:pb-3">
                  <div className="flex items-center gap-3">
                    <span className="font-serif text-xl text-cream-900 w-12">{note}</span>
                    <span className={`mastery-badge ${
                      mastery >= 3 ? 'mastered' : mastery >= 2 ? 'familiar' : 'learning'
                    }`}>
                      {getMasteryLabel(mastery)}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className={`font-sans font-medium ${
                      accuracy >= 80 ? 'text-sage-500' : accuracy >= 50 ? 'text-cream-800' : 'text-coral-500'
                    }`}>
                      {attempts > 0 ? `${accuracy}%` : '—'}
                    </p>
                    <p className="text-xs text-cream-800">
                      {attempts} attempts
                    </p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>

        {/* Interval Mastery Progress */}
        <section className="mb-8">
          <h2 className="font-serif text-xl text-cream-900 mb-4">
            Interval Mastery
          </h2>
          
          <Card className="border-wood-200 bg-white shadow-sm rounded-card mb-4">
            <CardContent className="py-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-sans text-sm text-cream-800">
                  {intervalStats.masteredCount} of {intervalStats.totalIntervals} intervals mastered
                </span>
                <span className="font-serif text-lg text-cream-900">
                  {Math.round((intervalStats.masteredCount / intervalStats.totalIntervals) * 100)}%
                </span>
              </div>
              <Progress 
                value={(intervalStats.masteredCount / intervalStats.totalIntervals) * 100} 
                className="h-3 bg-cream-300"
                indicatorClassName="bg-[#5F8D4E]"
              />
            </CardContent>
          </Card>

          <Card className="border-wood-200 bg-white shadow-sm rounded-card">
            <CardContent className="divide-y divide-wood-200">
              {intervalStatsDetailed.map(({ interval, accuracy, attempts, mastery }) => (
                <div key={interval} className="flex items-center justify-between py-3 first:pt-3 last:pb-3">
                  <div className="flex items-center gap-3">
                    <span className="font-serif text-xl text-cream-900 w-16">{interval}</span>
                    <span className={`mastery-badge ${
                      mastery >= 3 ? 'mastered' : mastery >= 2 ? 'familiar' : 'learning'
                    }`}>
                      {getMasteryLabel(mastery)}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className={`font-sans font-medium ${
                      accuracy >= 80 ? 'text-sage-500' : accuracy >= 50 ? 'text-cream-800' : 'text-coral-500'
                    }`}>
                      {attempts > 0 ? `${accuracy}%` : '—'}
                    </p>
                    <p className="text-xs text-cream-800">
                      {attempts} attempts
                    </p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>
      </div>
    </motion.div>
  );
});

export default ProgressPage;
